// components/advertiser-wallet/Modals.tsx
import React from "react";
import { CheckCircle2, Info } from "lucide-react";
import { ModalType } from "./types";
import ModalShell from "./modal-shell";
import Toggle from "./toggle";

interface ModalsProps {
  modal: ModalType;
  thresholdInput: string;
  emailNotif: boolean;
  whatsappNotif: boolean;
  customFundInput: string;
  quickFund: number;
  quickFundOptions: number[];
  minTopup: number;
  maxTopup: number;
  vatAmount: number;
  totalToPay: number;
  addedAmount: number;
  isAddingFunds?: boolean;
  isSavingThreshold?: boolean;
  formatNaira: (n: number) => string;
  onClose: () => void;
  onThresholdChange: (value: string) => void;
  onEmailNotifChange: (v: boolean) => void;
  onWhatsappNotifChange: (v: boolean) => void;
  onSaveThreshold: () => void;
  onCustomFundChange: (value: string) => void;
  onQuickFundSelect: (amount: number) => void;
  onConfirmAddFunds: () => void;
}

const Modals: React.FC<ModalsProps> = ({
  modal,
  thresholdInput,
  emailNotif,
  whatsappNotif,
  customFundInput,
  quickFund,
  quickFundOptions,
  minTopup,
  maxTopup,
  vatAmount,
  totalToPay,
  addedAmount,
  isAddingFunds,
  isSavingThreshold,
  formatNaira,
  onClose,
  onThresholdChange,
  onEmailNotifChange,
  onWhatsappNotifChange,
  onSaveThreshold,
  onCustomFundChange,
  onQuickFundSelect,
  onConfirmAddFunds,
}) => (
  <>
    {/* Low Balance Modal */}
    {modal === "lowBalance" && (
      <ModalShell
        title="Low Balance Threshold"
        subtitle="Set low balance alert threshold"
        onClose={onClose}
      >
        <label className="text-xs font-medium text-gray-600">Alert Threshold</label>
        <div className="flex items-center border border-gray-200 rounded-xl px-3.5 py-3 mt-1.5">
          <span className="text-gray-400 mr-1.5">₦</span>
          <input
            value={thresholdInput}
            onChange={(e) => onThresholdChange(e.target.value.replace(/[^\d]/g, ""))}
            inputMode="numeric"
            className="w-full outline-none text-sm"
          />
        </div>

        <p className="text-xs font-medium text-gray-600 mt-5">Notification Preferences</p>
        <p className="text-xs text-gray-400 mt-1">
          You'll be notified when your wallet balance falls to or below this amount. You can
          set notification preferences under Notifications.
        </p>

        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-700">Email Notifications</span>
          <Toggle checked={emailNotif} onChange={onEmailNotifChange} />
        </div>
        {/* <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-gray-700">WhatsApp Notifications</span>
          <Toggle checked={whatsappNotif} onChange={onWhatsappNotifChange} />
        </div> */}

        <button
          onClick={onSaveThreshold}
          disabled={isSavingThreshold}
          className="w-full bg-black text-white rounded-xl py-3 mt-6 text-sm font-medium hover:bg-gray-900 disabled:opacity-60"
        >
          {isSavingThreshold ? "Saving..." : "Save Changes"}
        </button>
      </ModalShell>
    )}

    {/* Add Funds Modal */}
    {modal === "addFundsAmount" && (
      <ModalShell title="Add Funds" subtitle="Add funds to your wallet" onClose={onClose}>
        <label className="text-xs font-medium text-gray-600">Enter Funding Amount</label>
        <div className="flex items-center border border-gray-200 rounded-xl px-3.5 py-3 mt-1.5">
          <span className="text-gray-400 mr-1.5">₦</span>
          <input
            value={customFundInput}
            onChange={(e) => onCustomFundChange(e.target.value)}
            inputMode="numeric"
            className="w-full outline-none text-sm"
          />
        </div>
        <p className="text-xs text-gray-400 mt-1.5">
          A 7.5% VAT will be added to your funding amount. Top-up amount must be between{" "}
          {formatNaira(minTopup)} and {formatNaira(maxTopup)}.
        </p>

        <div className="flex flex-wrap gap-2 mt-3">
          {quickFundOptions.map((amt) => (
            <button
              key={amt}
              onClick={() => onQuickFundSelect(amt)}
              className={`px-4 py-2 rounded-full text-sm border ${
                quickFund === amt
                  ? "border-emerald-500 text-emerald-600 bg-emerald-50"
                  : "border-gray-200 text-gray-700"
              }`}
            >
              {formatNaira(amt)}
            </button>
          ))}
        </div>
        {quickFund > 0 && quickFund < minTopup && (
          <p className="text-xs text-orange-500 mt-2">Minimum top-up amount is {formatNaira(minTopup)}.</p>
        )}
        {quickFund > maxTopup && (
          <p className="text-xs text-orange-500 mt-2">Maximum top-up amount is {formatNaira(maxTopup)}.</p>
        )}

        <div className="bg-gray-50 rounded-xl px-4 py-3.5 mt-4">
          <p className="text-sm font-semibold text-gray-900 mb-2">Payment Summary</p>
          <div className="border-t border-gray-200 pt-2.5 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Funding amount</span>
              <span className="text-gray-900">{formatNaira(quickFund || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">VAT at 7.5%</span>
              <span className="text-gray-900">{formatNaira(vatAmount)}</span>
            </div>
            <div className="border-t border-gray-200 pt-2.5 flex items-center justify-between text-sm">
              <span className="font-semibold text-gray-900">Total to pay</span>
              <span className="font-semibold text-emerald-600">{formatNaira(totalToPay)}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 items-start bg-gray-50 rounded-xl px-3.5 py-3 mt-4 text-xs text-gray-500">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>Always maintain sufficient funds to keep reward programs active.</span>
        </div>

        <button
          onClick={onConfirmAddFunds}
          disabled={!quickFund || quickFund < minTopup || quickFund > maxTopup || isAddingFunds}
          className="w-full bg-black text-white rounded-xl py-3 mt-6 text-sm font-medium hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isAddingFunds ? "Processing..." : `Pay ${formatNaira(totalToPay)}`}
        </button>
      </ModalShell>
    )}

    {/* Success Modal */}
    {modal === "addFundsSuccess" && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 sm:p-8 text-center">
          <h3 className="font-semibold text-black text-base">
            {formatNaira(addedAmount)} has been added to your advertiser wallet.
          </h3>
          <p className="text-sm text-gray-400 mt-2">
            Funds have been added successfully and will be used to power active reward
            programs across VouchLinks.
          </p>

          <div className="w-20 h-20 rounded-full border-4 border-blue-100 flex items-center justify-center mx-auto my-6">
            <CheckCircle2 size={36} className="text-blue-500" />
          </div>

          <button
            onClick={onClose}
            className="w-full bg-black text-white rounded-xl py-3 text-sm font-medium hover:bg-gray-900"
          >
            Close
          </button>
        </div>
      </div>
    )}
  </>
);

export default Modals;